/**
 * Jawg Places API Service
 *
 * Client for Jawg Places API autocomplete and geocoding.
 * Provides location search functionality for map centering and filtering.
 *
 * API Documentation: https://www.jawg.io/docs/apidocs/places/autocomplete/
 *
 * Usage:
 * ```typescript
 * import { jawgPlaces } from '@/services/jawg-places';
 *
 * // Search for locations
 * const suggestions = await jawgPlaces.autocomplete('Nashville');
 *
 * // Use first result to center map
 * if (suggestions.length > 0) {
 *   const { lat, lng } = suggestions[0];
 *   map.setCenter({ lat, lng });
 * }
 * ```
 */

import type {
  JawgAutocompleteResponse,
  JawgSuggestion,
  JawgAutocompleteOptions,
  JawgConfig,
  JawgFeature,
} from '@/types/jawg';

/**
 * Two-letter US state and territory codes (50 states + DC), used to detect a
 * trailing state in a query.
 */
const US_STATE_CODES = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'fl', 'ga', 'hi', 'id',
  'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms',
  'mo', 'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok',
  'or', 'pa', 'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv',
  'wi', 'wy', 'dc',
]);

/**
 * Insert a comma before a trailing two-letter US state code.
 *
 * Jawg's geocoder returns nothing for a bare "<city> <state-abbr>" query
 * (e.g. "columbia tn") but handles the comma form ("columbia, tn"), so this
 * rewrites the former into the latter. Queries that already contain a comma,
 * or that don't end in a state code, are returned unchanged.
 *
 * @param text - Raw search query
 * @returns The query with a comma inserted before a trailing state code, if any
 */
export function normalizeStateAbbreviation(text: string): string {
  const trimmed = text.trim();
  if (trimmed.includes(',')) {
    return text;
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length < 2) {
    return text;
  }

  // Known tradeoff: a few state codes are also common English words as a
  // trailing token (in, or, me, la, ok, hi). "north la" becomes "north, la".
  // We accept this — the rewrite only ever helps Jawg parse a state-qualified
  // query, and the worst case (a genuine non-state trailing word that happens
  // to be a code) is a slightly-off result, not an error.
  const lastWord = parts[parts.length - 1];
  if (!US_STATE_CODES.has(lastWord.toLowerCase())) {
    return text;
  }

  return `${parts.slice(0, -1).join(' ')}, ${lastWord}`;
}

/**
 * Default Jawg API configuration
 */
const DEFAULT_CONFIG: Required<JawgConfig> = {
  // NOTE: This token is publicly visible in client-side code
  // Jawg recommends using domain restrictions to prevent abuse
  accessToken: '6d9Ttas5NExIAQwp5cc78u8s5nFEeH4fyvM1uuGCEp4vSXM5pswxc5Hsb9ac1vve',
  baseUrl: 'https://api.jawg.io/places/v1',
  timeout: 5000,
};

/**
 * Jawg Places API Client
 */
export class JawgPlacesService {
  private config: Required<JawgConfig>;

  constructor(config: Partial<JawgConfig> = {}) {
    this.config = {
      ...DEFAULT_CONFIG,
      ...config,
    };
  }

  /**
   * Search for locations using autocomplete
   *
   * @param options - Search options
   * @returns Array of parsed suggestions
   * @throws Error if API request fails
   */
  async autocomplete(
    options: JawgAutocompleteOptions
  ): Promise<JawgSuggestion[]> {
    const { text, country = 'USA', layers = ['postalcode', 'county', 'locality'], limit = 10, focusLat, focusLng } = options;

    // Build query parameters
    const params = new URLSearchParams({
      // Rewrite "<city> <state-abbr>" -> "<city>, <state-abbr>" so Jawg parses it
      text: normalizeStateAbbreviation(text),
      'access-token': this.config.accessToken,
      'boundary.country': country,
      layers: layers.join(','),
      size: limit.toString(),
    });

    // Bias ranking toward a focus point (e.g. the user's location) so an
    // ambiguous query like a bare "columbia" surfaces the nearest match first.
    if (focusLat !== undefined && focusLng !== undefined) {
      params.set('focus.point.lat', focusLat.toString());
      params.set('focus.point.lon', focusLng.toString());
    }

    const url = `${this.config.baseUrl}/autocomplete?${params.toString()}`;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Jawg API error: ${response.status} ${response.statusText}`);
      }

      const data: JawgAutocompleteResponse = await response.json();

      // Parse and deduplicate results
      return this.parseSuggestions(data.features);
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Jawg API request timeout');
        }
        throw error;
      }
      throw new Error('Unknown error during Jawg API request');
    }
  }

  /**
   * Parse raw API features into UI-friendly suggestions
   *
   * Handles deduplication of results with same label by appending county.
   * Matches legacy implementation behavior.
   *
   * @param features - Raw API features
   * @returns Parsed suggestions
   */
  private parseSuggestions(features: JawgFeature[]): JawgSuggestion[] {
    const labelCounts = new Map<string, number>();
    const suggestions: JawgSuggestion[] = [];

    // First pass: count duplicate labels
    features.forEach((feature) => {
      const label = feature.properties.label;
      labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    });

    // Second pass: build suggestions with deduplication
    features.forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates;
      const baseLabel = feature.properties.label;
      const county = feature.properties.county;
      const isDuplicate = (labelCounts.get(baseLabel) ?? 0) > 1;

      // If label appears multiple times, append county to disambiguate
      const label = isDuplicate && county ? `${baseLabel}, ${county}` : baseLabel;

      suggestions.push({
        label,
        lat,
        lng,
        county,
        layer: feature.properties.layer,
        feature,
      });
    });

    return suggestions;
  }

  /**
   * Convenience method: Search by text string
   *
   * @param text - Search query
   * @returns Array of suggestions
   */
  async search(text: string): Promise<JawgSuggestion[]> {
    return this.autocomplete({ text });
  }
}

/**
 * Singleton instance for application-wide use
 */
export const jawgPlaces = new JawgPlacesService();
